import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import Navigation from '../../components/Navigation';
import { GET_ME, UPDATE_COFFEE_CHAT_SETTINGS } from '../../graphql/user';
import { GET_MY_COFFEE_MATCHES, DECLINE_COFFEE_CHAT_MATCH } from '../../graphql/GET_MY_COFFEE_MATCHES';
import { CREATE_ROOM } from '../../graphql/CREATE_ROOM';
import { CREATE_MESSAGE } from '../../graphql/CREATE_MESSAGE';
import type { IClientUser } from '../../interfaces/user';

const CoffeeChatSettings: React.FC<IClientUser> = (props: IClientUser) => {
  const [activeTab, setActiveTab] = useState<'matches' | 'history'>('matches');
  const [selectedFrequency, setSelectedFrequency] = useState<string>('monthly');
  const [availability, setAvailability] = useState<string>('open_to_new_matches');
  const [autoIcebreaker, setAutoIcebreaker] = useState<string>('yes');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [historySearchTerm, setHistorySearchTerm] = useState<string>('');
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [showAvailabilityDropdown, setShowAvailabilityDropdown] = useState(false);
  const [showIcebreakerDropdown, setShowIcebreakerDropdown] = useState(false);
  const [expandedReasons, setExpandedReasons] = useState(false);

  const { data, loading } = useQuery(GET_ME);
  const { data: matchesData, loading: matchesLoading, refetch: refetchMatches } = useQuery(GET_MY_COFFEE_MATCHES);
  const [updateCoffeeChatSettings] = useMutation(UPDATE_COFFEE_CHAT_SETTINGS);
  const [declineCoffeeChatMatch] = useMutation(DECLINE_COFFEE_CHAT_MATCH);
  const [createRoom] = useMutation(CREATE_ROOM);
  const [createMessage] = useMutation(CREATE_MESSAGE);

  useEffect(() => {
    if (data?.me) {
      if (data.me.coffeeChatFrequency) {
        setSelectedFrequency(data.me.coffeeChatFrequency);
      }
      if (data.me.coffeeChatAvailability) {
        setAvailability(data.me.coffeeChatAvailability);
      }
      if (data.me.coffeeChatAutoIcebreaker) {
        setAutoIcebreaker(data.me.coffeeChatAutoIcebreaker);
      }
      if (data.me.coffeeChatPreferences) {
        setSelectedPreferences(data.me.coffeeChatPreferences);
      }
    }
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown')) {
        setShowFrequencyDropdown(false);
        setShowAvailabilityDropdown(false);
        setShowIcebreakerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const frequencyOptions = [
    { value: 'weekly', label: 'Once a Week' },
    { value: 'monthly', label: 'Once a Month' },
    { value: 'off', label: 'Off' }
  ];

  const availabilityOptions = [
    { value: 'open_to_new_matches', label: 'Open to new matches' },
    { value: 'not_available', label: 'Not available' }
  ];

  const icebreakerOptions = [
    { value: 'yes', label: 'Yes, add a friendly icebreaker' },
    { value: 'no', label: 'No, let me start the conversation' }
  ];

  const handleAddPreference = () => {
    if (searchTerm.trim() && !selectedPreferences.includes(searchTerm.trim())) {
      setSelectedPreferences([...selectedPreferences, searchTerm.trim()]);
      setSearchTerm('');
    }
  };

  const handleRemovePreference = (preference: string) => {
    setSelectedPreferences(selectedPreferences.filter(p => p !== preference));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateCoffeeChatSettings({
        variables: {
          settings: {
            frequency: selectedFrequency,
            availability,
            autoIcebreaker,
            preferences: selectedPreferences
          }
        },
        refetchQueries: [{ query: GET_ME }]
      });
      setIsEditing(false);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getOtherPerson = (match: any) => {
    if (!match || !data?.me) return null;
    const currentUserId = parseInt(data.me.id);
    return match.personAId === currentUserId ? match.personB : match.personA;
  };

  const latestMatch = matchesData?.myCoffeeMatches?.find(match => match.status === 'pending');
  const otherPerson = latestMatch ? getOtherPerson(latestMatch) : null;

  const isNewMatch = (match: any) => {
    if (!match || !match.matchedAt) return false;
    const matchTime = new Date(match.matchedAt).getTime();
    const now = Date.now();
    return (now - matchTime) < 86400000;
  };

  const getRelativeTime = (date: string) => {
    const now = Date.now();
    const matchTime = new Date(date).getTime();
    const diff = now - matchTime;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours} h`;
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const filteredMatches = useMemo(() => {
    if (!matchesData?.myCoffeeMatches) return [];
    if (!historySearchTerm.trim()) return matchesData.myCoffeeMatches;

    const searchLower = historySearchTerm.toLowerCase();
    return matchesData.myCoffeeMatches.filter((match) => {
      const otherPerson = getOtherPerson(match);
      if (!otherPerson) return false;
      const name = otherPerson.profile?.name || otherPerson.email || '';
      return name.toLowerCase().includes(searchLower);
    });
  }, [matchesData, historySearchTerm, data]);

  const handleRequestAnotherMatch = async () => {
    if (!latestMatch) return;

    try {
      await declineCoffeeChatMatch({
        variables: { matchId: parseFloat(latestMatch.id) }
      });
      await refetchMatches();
      toast.success('Match declined. You\'ll be included in the next matching round!');
    } catch (error) {
      console.error('Error declining match:', error);
      toast.error('Failed to decline match. Please try again.');
    }
  };

  const handleLetsChat = async (match?: any) => {
    const targetMatch = match || latestMatch;
    if (!targetMatch) return;

    const targetPerson = getOtherPerson(targetMatch);
    if (!targetPerson) return;

    try {
      const result = await createRoom({
        variables: {
          memberIds: [Number(targetPerson.id)],
          roomType: 'single',
          chatType: 'coffee'
        }
      });

      await declineCoffeeChatMatch({
        variables: { matchId: parseFloat(targetMatch.id) }
      });

      await refetchMatches();

      const roomId = result.data?.createChatRoom?.id;
      if (roomId) {
        await createMessage({
          variables: {
            roomId: Number(roomId),
            content: 'Happy to connect! Looking forward to learning about what youre creating.'
          }
        });
        toast.success('Coffee chat request sent! Redirecting to your conversation...');
        setTimeout(() => {
          window.location.href = `/messages/${roomId}`;
        }, 1500);
      } else {
        toast.success('Coffee chat request sent! Redirecting to messages...');
        setTimeout(() => {
          window.location.href = '/messages';
        }, 1500);
      }
    } catch (error) {
      console.error('Error starting coffee chat:', error);
      toast.error('Failed to start coffee chat. Please try again.');
    }
  };

  const handleNavigateToChat = (match: any) => {
    window.location.href = '/messages';
  };

  if (loading) {
    return (
      <Fragment>
        <Navigation {...props} />
        <div className="container">
          <div className="section">
            <div className="has-text-centered">
              <div className="button is-loading is-white">Loading...</div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  const matchCount = matchesData?.myCoffeeMatches?.length || 0;

  return (
    <Fragment>
      <main className="page-coffee-chat-settings">
        <Navigation {...props} />
        <section className="container">
          <div className="columns section">
            <div className="column is-2"></div>
            <div className="column is-6">
              <div className="card is-rounded">
                <div className="card-content">
                  <div className="coffee-chat-header">
                    <div className="header-row">
                      <h1 className="page-title">Coffee Chat Settings</h1>
                    </div>
                    <p className="page-subtitle">See who you've been matched with for a quick creative chat</p>
                  </div>

                  <div className="coffee-chat-tabs">
                    <div className="tabs-header">
                      <button
                        className={`tab-button ${activeTab === 'matches' ? 'active' : ''}`}
                        onClick={() => setActiveTab('matches')}
                      >
                        Coffee Matches
                      </button>
                      <button
                        className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                      >
                        History {matchCount > 0 && <span className="tab-count">{matchCount}</span>}
                      </button>
                    </div>

                    <div className="tabs-content">
                      {activeTab === 'matches' && (
                        <div className="settings-content">
                          <div className="settings-header">
                            <h2 className="settings-title">Settings</h2>
                            <button className="edit-icon" onClick={() => setIsEditing(!isEditing)}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill={isEditing ? '#6366F1' : '#9CA3AF'} />
                              </svg>
                            </button>
                          </div>

                          <div className="settings-section">
                            <label className="settings-label">Coffee Match Frequency</label>
                            <div className={`dropdown ${showFrequencyDropdown ? 'is-active' : ''}`}>
                              <div className="dropdown-trigger">
                                <button className="button is-fullwidth dropdown-button" aria-haspopup="true" aria-controls="frequency-menu" disabled={!isEditing} onClick={() => setShowFrequencyDropdown(!showFrequencyDropdown)}>
                                  <span className="dropdown-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M13 10V3L4 14h7v7l9-11h-7z" fill="#6366F1" />
                                    </svg>
                                  </span>
                                  <span className="dropdown-text">{frequencyOptions.find(opt => opt.value === selectedFrequency)?.label}</span>
                                  <span className="icon is-small dropdown-arrow">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                      <path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                </button>
                              </div>
                              <div className="dropdown-menu" id="frequency-menu" role="menu">
                                <div className="dropdown-content">
                                  {frequencyOptions.map(option => (
                                    <a key={option.value} className={`dropdown-item ${selectedFrequency === option.value ? 'is-active' : ''}`} onClick={() => { setSelectedFrequency(option.value); setShowFrequencyDropdown(false); }}>
                                      {option.label}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="settings-section">
                            <label className="settings-label">Availability</label>
                            <div className={`dropdown ${showAvailabilityDropdown ? 'is-active' : ''}`}>
                              <div className="dropdown-trigger">
                                <button className="button is-fullwidth dropdown-button" aria-haspopup="true" aria-controls="availability-menu" disabled={!isEditing} onClick={() => setShowAvailabilityDropdown(!showAvailabilityDropdown)}>
                                  <span className="dropdown-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <circle cx="12" cy="12" r="10" stroke="#6366F1" strokeWidth="2" fill="none" />
                                      <path d="M9 12l2 2 4-4" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                  <span className="dropdown-text">{availabilityOptions.find(opt => opt.value === availability)?.label}</span>
                                  <span className="icon is-small dropdown-arrow">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                      <path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                </button>
                              </div>
                              <div className="dropdown-menu" id="availability-menu" role="menu">
                                <div className="dropdown-content">
                                  {availabilityOptions.map(option => (
                                    <a key={option.value} className={`dropdown-item ${availability === option.value ? 'is-active' : ''}`} onClick={() => { setAvailability(option.value); setShowAvailabilityDropdown(false); }}>
                                      {option.label}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="settings-section">
                            <label className="settings-label">Match Preferences</label>
                            <div className="search-container">
                              <div className="search-input-wrapper">
                                <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <circle cx="11" cy="11" r="8" stroke="#9CA3AF" strokeWidth="2" />
                                  <path d="m21 21-4.35-4.35" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <input type="text" className="input search-input" placeholder="Search to add" value={searchTerm} disabled={!isEditing} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddPreference()} />
                              </div>
                            </div>
                            <div className="preference-tags">
                              {selectedPreferences.map((preference, index) => (
                                <span key={index} className="tag page-add-project-card-content-tag is-primary m-b-1">
                                  {preference}
                                  {isEditing && <button className="delete is-small" onClick={() => handleRemovePreference(preference)} />}
                                </span>
                              ))}
                              {isEditing && searchTerm && (
                                <button className="button is-small add-tag-btn" onClick={handleAddPreference}>
                                  <span className="icon is-small">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M12 5v14m-7-7h14" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                  <span>Add "{searchTerm}"</span>
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="settings-section">
                            <label className="settings-label">Auto-Icebreaker</label>
                            <div className={`dropdown ${showIcebreakerDropdown ? 'is-active' : ''}`}>
                              <div className="dropdown-trigger">
                                <button className="button is-fullwidth dropdown-button" aria-haspopup="true" aria-controls="icebreaker-menu" disabled={!isEditing} onClick={() => setShowIcebreakerDropdown(!showIcebreakerDropdown)}>
                                  <span className="dropdown-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <circle cx="12" cy="12" r="10" fill="#6366F1" />
                                      <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                  <span className="dropdown-text">{icebreakerOptions.find(opt => opt.value === autoIcebreaker)?.label}</span>
                                  <span className="icon is-small dropdown-arrow">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                      <path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                </button>
                              </div>
                              <div className="dropdown-menu" id="icebreaker-menu" role="menu">
                                <div className="dropdown-content">
                                  {icebreakerOptions.map(option => (
                                    <a key={option.value} className={`dropdown-item ${autoIcebreaker === option.value ? 'is-active' : ''}`} onClick={() => { setAutoIcebreaker(option.value); setShowIcebreakerDropdown(false); }}>
                                      {option.label}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>

                          {isEditing && (
                            <div className="save-button-container">
                              <button className={`button is-primary save-button ${isSaving ? 'is-loading' : ''}`} onClick={handleSave} disabled={isSaving}>
                                Save
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === 'history' && (
                        <div className="history-content">
                          <div className="history-header">
                            <h2 className="history-title">Match History <span className="history-count">{matchCount}</span></h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              {isNewMatch(latestMatch) && (
                                <span className="new-match-badge">● New Match</span>
                              )}
                              <button
                                onClick={() => setExpandedReasons(!expandedReasons)}
                                style={{
                                  background: 'transparent',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  padding: '6px 12px',
                                  fontSize: '13px',
                                  color: '#6b7280',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#f9fafb';
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  {expandedReasons ? (
                                    <path d="M5 15l7-7 7 7" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  ) : (
                                    <path d="M19 9l-7 7-7-7" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  )}
                                </svg>
                                <span>{expandedReasons ? 'Collapse All' : 'Expand All'}</span>
                              </button>
                            </div>
                          </div>

                          <div className="history-search">
                            <div className="search-input-wrapper">
                              <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <circle cx="11" cy="11" r="8" stroke="#9CA3AF" strokeWidth="2" />
                                <path d="m21 21-4.35-4.35" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <input type="text" className="input search-input" placeholder="Field, Name, Location" value={historySearchTerm} onChange={(e) => setHistorySearchTerm(e.target.value)} />
                            </div>
                          </div>

                          <div className="history-list">
                            {!matchesLoading && filteredMatches.length === 0 && (
                              <div className="no-matches">
                                <p>No matches found</p>
                              </div>
                            )}

                            {!matchesLoading && filteredMatches.map((match) => {
                              const person = getOtherPerson(match);
                              if (!person) return null;

                              const isPending = match.status === 'pending';
                              const isAccepted = match.status === 'accepted';
                              const matchIsNew = isNewMatch(match);

                              return (
                                <div key={match.id} className="history-item" style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '16px', transition: 'background 0.2s' }} onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#fafafa';
                                }} onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'white';
                                }}>
                                  <div style={{ position: 'relative' }}>
                                    <img src={person.avatar?.fileUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'} alt={person.profile?.name || person.email} style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }} />
                                    {matchIsNew && isPending && (
                                      <div style={{ position: 'absolute', top: '-4px', right: '-4px', width: '16px', height: '16px', background: '#ef4444', borderRadius: '50%', border: '2px solid white' }} />
                                    )}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                      <span style={{ fontWeight: 600, fontSize: '15px', color: '#1f2937' }}>{person.profile?.name || person.email}</span>
                                      <span style={{ fontSize: '13px', color: '#9ca3af' }}>{getRelativeTime(match.matchedAt)}</span>
                                    </div>
                                    {match.reason && (
                                      <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px', lineHeight: '1.5' }}>
                                        {expandedReasons || match.reason.length <= 80
                                          ? match.reason
                                          : match.reason.substring(0, 80) + '...'}
                                      </div>
                                    )}
                                    {isPending && (
                                      <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>Waiting for response...</div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {isPending && (
                                      <button
                                        style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                                        onClick={async () => await handleLetsChat(match)}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = '#4f46e5';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = '#6366f1';
                                        }}
                                      >
                                        Let's Chat!
                                      </button>
                                    )}
                                    {isAccepted && (
                                      <button
                                        style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        onClick={() => handleNavigateToChat(match)}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = '#e5e7eb';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = '#f3f4f6';
                                        }}
                                      >
                                        <span>☕</span>
                                        <span>Chat</span>
                                      </button>
                                    )}
                                    <button
                                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '6px', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                                      onClick={() => handleNavigateToChat(match)}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f3f4f6';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                      }}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="column is-4">
              {!matchesLoading && latestMatch && otherPerson && (
                <div className="card is-rounded match-card">
                  <div className="card-content">
                    <div className="match-header">
                      <span className="match-badge">✨ New Match</span>
                    </div>
                    <div className="match-content">
                      <h3 className="match-title">You've been matched with {otherPerson.profile?.name}.</h3>
                      <p className="match-subtitle">Want to connect over coffee?</p>
                      <div className="match-profile">
                        <div className="match-avatar">
                          <img src={otherPerson.avatar?.fileUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'} alt={otherPerson.profile?.firstName || otherPerson.email} />
                        </div>
                        <div className="match-info">
                          <h4 className="match-name">{otherPerson.profile?.name || ''}</h4>
                          <p className="match-role">{otherPerson.professions?.[0]?.name || 'Creative'}</p>
                        </div>
                      </div>
                      <div className="match-actions">
                        <button className='button is-primary is-fullwidth match-button' onClick={async () => await handleLetsChat()}>
                          Let's Chat!
                        </button>
                        <button className="button is-light is-fullwidth match-button-secondary" onClick={handleRequestAnotherMatch}>
                          Request Another Match
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!matchesLoading && !latestMatch && selectedFrequency !== 'off' && (
                <div className="card is-rounded no-match-card">
                  <div className="card-content">
                    <div className="no-match-content">
                      <div className="no-match-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <path d="M9.5 7h5m-5 4h5m-5 4h5M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <h3 className="no-match-title">
                        No matching this {selectedFrequency === 'weekly' ? 'week' : 'month'}
                      </h3>
                      <p className="no-match-subtitle">
                        We'll find you a perfect match {selectedFrequency === 'weekly' ? 'next week' : 'next month'}.
                        Keep an eye on your inbox!
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </Fragment>
  );
};

export default CoffeeChatSettings;
